import { MARD_291, PALETTE_BRAND, readableTextColor } from './palette';
import { drawShareCode } from './shareCode';
import { watermarkCells } from './watermark';
import type { ColorStat, PatternProject, PatternWatermark } from '../types';

const EXPORT_CELL_SIZE = 36;
const MIN_EXPORT_CELL_SIZE = 8;
const MAX_EXPORT_SIDE = 12_000;
const MAX_EXPORT_PIXELS = 96_000_000;
const SHEET_PADDING = 48;
const HEADER_HEIGHT = 190;
const LEGEND_GAP = 66;
const LEGEND_HEADING_HEIGHT = 58;
const LEGEND_ROW_HEIGHT = 58;
const FOOTER_HEIGHT = 78;

interface ExportLayout {
  cellSize: number;
  rulerSize: number;
  boardWidth: number;
  boardHeight: number;
  patternWidth: number;
  patternHeight: number;
  legendColumns: number;
  legendRows: number;
  width: number;
  height: number;
}

interface ExportIdentity {
  authorName: string;
  watermark?: PatternWatermark | null;
  sharePatternId?: string;
}

function formatDate(value: number) {
  const date = new Date(value);
  const pad = (number: number) => String(number).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function getRulerSize(cellSize: number) {
  return Math.max(42, Math.min(72, Math.floor(cellSize * 2.15)));
}

function getLegendColumns(_count: number) {
  return 9;
}

function buildStats(project: PatternProject): ColorStat[] {
  const counts = new Map<number, number>();
  let total = 0;
  for (let index = 0; index < project.colorIndices.length; index += 1) {
    if (project.empty[index]) continue;
    const paletteIndex = project.colorIndices[index];
    counts.set(paletteIndex, (counts.get(paletteIndex) || 0) + 1);
    total += 1;
  }
  return [...counts.entries()].map(([paletteIndex, count]) => ({
    ...MARD_291[paletteIndex],
    paletteIndex,
    count,
    completed: 0,
    percent: total ? count / total * 100 : 0
  })).sort((left, right) => left.code.localeCompare(right.code, 'en', { numeric: true, sensitivity: 'base' }));
}

function measureLayout(project: PatternProject, statCount: number, cellSize: number): ExportLayout {
  const rulerSize = getRulerSize(cellSize);
  const boardWidth = project.width * cellSize;
  const boardHeight = project.height * cellSize;
  const patternWidth = boardWidth + rulerSize * 2;
  const patternHeight = boardHeight + rulerSize * 2;
  const legendColumns = getLegendColumns(statCount);
  const legendRows = Math.max(1, Math.ceil(statCount / legendColumns));
  const width = Math.max(980, patternWidth + SHEET_PADDING * 2);
  const height = HEADER_HEIGHT + patternHeight + LEGEND_GAP + LEGEND_HEADING_HEIGHT
    + legendRows * LEGEND_ROW_HEIGHT + FOOTER_HEIGHT;
  return {
    cellSize,
    rulerSize,
    boardWidth,
    boardHeight,
    patternWidth,
    patternHeight,
    legendColumns,
    legendRows,
    width,
    height
  };
}

export function getExportLayout(project: PatternProject, statCount: number) {
  let cellSize = EXPORT_CELL_SIZE;
  let layout = measureLayout(project, statCount, cellSize);
  while (cellSize > MIN_EXPORT_CELL_SIZE && (
    layout.width > MAX_EXPORT_SIDE
    || layout.height > MAX_EXPORT_SIDE
    || layout.width * layout.height > MAX_EXPORT_PIXELS
  )) {
    cellSize -= 1;
    layout = measureLayout(project, statCount, cellSize);
  }
  if (layout.width > MAX_EXPORT_SIDE || layout.height > MAX_EXPORT_SIDE || layout.width * layout.height > MAX_EXPORT_PIXELS) {
    throw new Error('图纸尺寸过大，暂时无法导出为一张 PNG，请适当减少行列数');
  }
  return layout;
}

function exportTextColor(hex: string) {
  return readableTextColor(hex) === '#ffffff' ? '#f4efe6' : '#172033';
}

function strokeLine(context: CanvasRenderingContext2D, x1: number, y1: number, x2: number, y2: number) {
  context.beginPath();
  context.moveTo(x1, y1);
  context.lineTo(x2, y2);
  context.stroke();
}

function drawRoundedRect(context: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number) {
  const safeRadius = Math.max(0, Math.min(radius, width / 2, height / 2));
  context.beginPath();
  context.roundRect(x, y, width, height, safeRadius);
}

function drawSheetBackground(context: CanvasRenderingContext2D, width: number, height: number) {
  context.fillStyle = '#f7f0dd';
  context.fillRect(0, 0, width, height);
  context.fillStyle = 'rgba(255, 255, 255, 0.74)';
  context.fillRect(24, 24, width - 48, height - 48);
}

function drawHeader(context: CanvasRenderingContext2D, project: PatternProject, stats: ColorStat[], width: number, identity: ExportIdentity, logo: HTMLImageElement | null) {
  const total = stats.reduce((sum, item) => sum + item.count, 0);
  context.fillStyle = '#172033';
  context.font = '700 42px system-ui, sans-serif';
  context.textAlign = 'left';
  context.textBaseline = 'top';
  context.fillText(project.name || '拼豆完整图纸', SHEET_PADDING, 34, Math.max(360, width - 430));

  context.fillStyle = '#526174';
  context.font = '500 23px system-ui, sans-serif';
  context.fillText(`@${identity.authorName}    网格：${project.width} × ${project.height}`, SHEET_PADDING, 88);
  context.font = '400 22px system-ui, sans-serif';
  context.fillText(`总豆数：${total}    颜色数：${stats.length}    生成时间：${formatDate(Date.now())}`, SHEET_PADDING, 124);

  const brandRight = width - SHEET_PADDING;
  if (logo) context.drawImage(logo, brandRight - 252, 32, 88, 88);
  context.fillStyle = '#172033';
  context.font = '700 30px system-ui, sans-serif';
  context.textAlign = 'right';
  context.fillText('伊小满拼豆', brandRight, 58);
  if (identity.sharePatternId) drawShareCode(context, identity.sharePatternId, brandRight, 130);
  context.textAlign = 'left';

  context.strokeStyle = 'rgba(23, 32, 51, 0.16)';
  context.lineWidth = 2;
  strokeLine(context, SHEET_PADDING, 166, width - SHEET_PADDING, 166);
}

async function loadBrandLogo() {
  return new Promise<HTMLImageElement | null>((resolve) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = '/logo.png';
  });
}

function getLabelStep(count: number, cellSize: number) {
  if (cellSize >= 14) return 1;
  if (count <= 156) return 13;
  if (count <= 320) return 26;
  return 52;
}

function drawRulers(context: CanvasRenderingContext2D, project: PatternProject, layout: ExportLayout, offsetX: number, offsetY: number) {
  const { cellSize, rulerSize, boardWidth, boardHeight, patternWidth, patternHeight } = layout;
  const boardX = offsetX + rulerSize;
  const boardY = offsetY + rulerSize;
  const fontSize = Math.max(12, Math.min(20, Math.floor(cellSize * 0.45)));
  const colStep = getLabelStep(project.width, cellSize);
  const rowStep = getLabelStep(project.height, cellSize);

  context.fillStyle = '#eaf4ff';
  context.fillRect(boardX, offsetY, boardWidth, rulerSize);
  context.fillRect(boardX, boardY + boardHeight, boardWidth, rulerSize);
  context.fillRect(offsetX, boardY, rulerSize, boardHeight);
  context.fillRect(boardX + boardWidth, boardY, rulerSize, boardHeight);

  context.strokeStyle = 'rgba(80, 112, 145, 0.26)';
  context.lineWidth = Math.max(1, Math.floor(cellSize * 0.035));
  context.strokeRect(boardX, boardY, boardWidth, boardHeight);
  context.strokeRect(offsetX + 0.5, offsetY + 0.5, patternWidth - 1, patternHeight - 1);

  context.fillStyle = '#5a8db8';
  context.font = `700 ${fontSize}px system-ui, sans-serif`;
  context.textAlign = 'center';
  context.textBaseline = 'middle';

  for (let col = 0; col < project.width; col += 1) {
    if (col !== 0 && col !== project.width - 1 && (col + 1) % colStep !== 0) continue;
    const x = boardX + (col + 0.5) * cellSize;
    const label = String(col + 1);
    context.fillText(label, x, offsetY + rulerSize / 2);
    context.fillText(label, x, boardY + boardHeight + rulerSize / 2);
  }
  for (let row = 0; row < project.height; row += 1) {
    if (row !== 0 && row !== project.height - 1 && (row + 1) % rowStep !== 0) continue;
    const y = boardY + (row + 0.5) * cellSize;
    const label = String(row + 1);
    context.fillText(label, offsetX + rulerSize / 2, y);
    context.fillText(label, boardX + boardWidth + rulerSize / 2, y);
  }
}

function drawBoardGuide(context: CanvasRenderingContext2D, project: PatternProject, layout: ExportLayout, boardX: number, boardY: number) {
  const { cellSize, boardWidth, boardHeight } = layout;
  context.strokeStyle = 'rgba(30, 46, 72, 0.15)';
  context.lineWidth = Math.max(0.5, Math.min(1.4, cellSize * 0.03));
  for (let col = 0; col <= project.width; col += 1) {
    const x = boardX + col * cellSize;
    strokeLine(context, x, boardY, x, boardY + boardHeight);
  }
  for (let row = 0; row <= project.height; row += 1) {
    const y = boardY + row * cellSize;
    strokeLine(context, boardX, y, boardX + boardWidth, y);
  }

  const dash = Math.max(4, cellSize * 0.32);
  const gap = Math.max(3, cellSize * 0.22);
  context.save();
  context.setLineDash([dash, gap]);
  context.strokeStyle = 'rgba(34, 40, 55, 0.42)';
  context.lineWidth = Math.max(1, Math.min(2, cellSize * 0.045));
  for (let col = 6.5; col < project.width; col += 13) {
    const x = boardX + col * cellSize;
    strokeLine(context, x, boardY, x, boardY + boardHeight);
  }
  for (let row = 6.5; row < project.height; row += 13) {
    const y = boardY + row * cellSize;
    strokeLine(context, boardX, y, boardX + boardWidth, y);
  }
  context.restore();

  context.strokeStyle = 'rgba(22, 26, 38, 0.80)';
  context.lineWidth = Math.max(1.2, Math.min(3, cellSize * 0.075));
  for (let col = 0; col <= project.width; col += 13) {
    const x = boardX + col * cellSize;
    strokeLine(context, x, boardY, x, boardY + boardHeight);
  }
  if (project.width % 13 !== 0) strokeLine(context, boardX + boardWidth, boardY, boardX + boardWidth, boardY + boardHeight);
  for (let row = 0; row <= project.height; row += 13) {
    const y = boardY + row * cellSize;
    strokeLine(context, boardX, y, boardX + boardWidth, y);
  }
  if (project.height % 13 !== 0) strokeLine(context, boardX, boardY + boardHeight, boardX + boardWidth, boardY + boardHeight);
}

function drawPattern(context: CanvasRenderingContext2D, project: PatternProject, layout: ExportLayout, offsetX: number, offsetY: number) {
  const { cellSize, rulerSize, boardWidth, boardHeight, patternWidth, patternHeight } = layout;
  const boardX = offsetX + rulerSize;
  const boardY = offsetY + rulerSize;
  context.fillStyle = '#ffffff';
  context.fillRect(offsetX, offsetY, patternWidth, patternHeight);
  drawRulers(context, project, layout, offsetX, offsetY);
  context.fillStyle = '#fffefd';
  context.fillRect(boardX, boardY, boardWidth, boardHeight);

  for (let row = 0; row < project.height; row += 1) {
    for (let col = 0; col < project.width; col += 1) {
      const index = row * project.width + col;
      if (project.empty[index]) continue;
      const color = MARD_291[project.colorIndices[index]];
      if (!color) continue;
      context.fillStyle = color.hex;
      context.fillRect(boardX + col * cellSize, boardY + row * cellSize, cellSize, cellSize);
    }
  }

  drawBoardGuide(context, project, layout, boardX, boardY);

  const codeFontSize = Math.max(8, Math.floor(cellSize * 0.32));
  context.font = `500 ${codeFontSize}px system-ui, sans-serif`;
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  for (let row = 0; row < project.height; row += 1) {
    for (let col = 0; col < project.width; col += 1) {
      const index = row * project.width + col;
      if (project.empty[index]) continue;
      const color = MARD_291[project.colorIndices[index]];
      if (!color) continue;
      context.fillStyle = exportTextColor(color.hex);
      context.fillText(color.code, boardX + (col + 0.5) * cellSize, boardY + (row + 0.5) * cellSize);
    }
  }
}

function drawWatermark(context: CanvasRenderingContext2D, watermark: PatternWatermark, layout: ExportLayout, offsetX: number, offsetY: number) {
  const boardX = offsetX + layout.rulerSize;
  const boardY = offsetY + layout.rulerSize;
  context.save();
  context.strokeStyle = 'rgba(23, 32, 51, 0.28)';
  context.lineWidth = Math.max(1, layout.cellSize * 0.045);
  watermarkCells(watermark).forEach((cell) => {
    const x = boardX + cell.col * layout.cellSize;
    const y = boardY + cell.row * layout.cellSize;
    const size = cell.size * layout.cellSize;
    context.fillStyle = MARD_291[cell.colorIndex]?.hex || '#172033';
    context.fillRect(x, y, size, size);
    context.strokeRect(x, y, size, size);
  });
  context.restore();
}

function drawLegend(context: CanvasRenderingContext2D, stats: ColorStat[], layout: ExportLayout, startY: number) {
  context.fillStyle = '#172033';
  context.font = '700 34px system-ui, sans-serif';
  context.textAlign = 'left';
  context.textBaseline = 'top';
  context.fillText('色号清单', SHEET_PADDING, startY);

  const gap = 10;
  const availableWidth = layout.width - SHEET_PADDING * 2;
  const columnWidth = (availableWidth - gap * (layout.legendColumns - 1)) / layout.legendColumns;
  const cardHeight = 46;
  const cardsY = startY + LEGEND_HEADING_HEIGHT;
  stats.forEach((item, index) => {
    const col = index % layout.legendColumns;
    const row = Math.floor(index / layout.legendColumns);
    const x = SHEET_PADDING + col * (columnWidth + gap);
    const y = cardsY + row * LEGEND_ROW_HEIGHT + 6;
    context.fillStyle = item.hex;
    drawRoundedRect(context, x, y, columnWidth, cardHeight, 10);
    context.fill();
    context.strokeStyle = 'rgba(23, 32, 51, 0.14)';
    context.lineWidth = 1;
    context.stroke();

    context.fillStyle = exportTextColor(item.hex);
    context.font = '700 16px system-ui, sans-serif';
    context.textBaseline = 'middle';
    context.textAlign = 'center';
    context.fillText(`${item.code} (${item.count.toLocaleString('zh-CN')})`, x + columnWidth / 2, y + cardHeight / 2, Math.max(40, columnWidth - 12));
  });
}

function drawFooter(context: CanvasRenderingContext2D, width: number, height: number) {
  context.fillStyle = '#7a4f34';
  context.font = '400 21px system-ui, sans-serif';
  context.textAlign = 'center';
  context.textBaseline = 'top';
  context.fillText(`${PALETTE_BRAND} 色号仅供手工参考，实际颜色请以材料为准`, width / 2, height - 50);
}

export async function renderPatternCanvas(project: PatternProject, identity: ExportIdentity = { authorName: '作者' }) {
  const stats = buildStats(project);
  const baseLayout = getExportLayout(project, stats.length);
  const watermark = identity.watermark;
  const watermarkScale = watermark ? Math.max(0.25, Math.min(4, Number(watermark.scale) || 1)) : 1;
  const extraLeft = watermark ? Math.max(0, -watermark.x) : 0;
  const extraTop = watermark ? Math.max(0, -watermark.y) : 0;
  const extraRight = watermark ? Math.max(0, watermark.x + watermark.width * watermarkScale - project.width) : 0;
  const extraBottom = watermark ? Math.max(0, watermark.y + watermark.height * watermarkScale - project.height) : 0;
  const contentWidth = baseLayout.patternWidth + (extraLeft + extraRight) * baseLayout.cellSize;
  const width = Math.max(980, contentWidth + SHEET_PADDING * 2);
  const height = baseLayout.height + (extraTop + extraBottom) * baseLayout.cellSize;
  if (width > MAX_EXPORT_SIDE || height > MAX_EXPORT_SIDE || width * height > MAX_EXPORT_PIXELS) throw new Error('水印距离图纸过远，导出尺寸超过上限，请把水印拖近一些');
  const layout = { ...baseLayout, width, height };
  const canvas = document.createElement('canvas');
  canvas.width = layout.width;
  canvas.height = layout.height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('无法创建导出画布');

  drawSheetBackground(context, layout.width, layout.height);
  const logo = await loadBrandLogo();
  drawHeader(context, project, stats, layout.width, identity, logo);
  const patternX = Math.floor((layout.width - contentWidth) / 2) + extraLeft * layout.cellSize;
  const patternY = HEADER_HEIGHT + extraTop * layout.cellSize;
  context.fillStyle = '#ffffff';
  context.fillRect(patternX - 8, patternY - 8, layout.patternWidth + 16, layout.patternHeight + 16);
  drawPattern(context, project, layout, patternX, patternY);
  if (identity.watermark) drawWatermark(context, identity.watermark, layout, patternX, patternY);
  const legendY = patternY + layout.patternHeight + extraBottom * layout.cellSize + LEGEND_GAP;
  drawLegend(context, stats, layout, legendY);
  drawFooter(context, layout.width, layout.height);
  return canvas;
}

export async function exportPatternPng(project: PatternProject, identity: ExportIdentity = { authorName: '作者' }) {
  const canvas = await renderPatternCanvas(project, identity);
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((value) => value ? resolve(value) : reject(new Error('PNG 生成失败，请缩小图纸后重试')), 'image/png', 1);
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  const suffix = identity.sharePatternId ? '分享图纸' : identity.watermark ? '带水印高清图纸' : '高清完整图纸';
  anchor.download = `${project.name.replace(/[\\/:*?"<>|]/g, '_')}-${suffix}.png`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
